import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, Location } from '@angular/common';
import {
    AfterContentChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    NgModule,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { Cms, IFilterResponse, ILink } from '@impact/data';
import { OverlayModule, OverlayService } from '@impactdk/ngx-overlay';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { UtilsModule } from '../../../lib/utils/utils.module';
import { BREAKPOINTS, BreakpointService } from '../../breakpoint/breakpoint.service';
import { ComponentLoaderModule } from '../../component-loader/component-loader.module';
import { HeaderService } from '../../core/header.service';
import { SettingsService, SiteIds } from '../../core/settings.service';
import { FilterService } from '../../filter-service/filter.service';
import { MobileFacetListComponent } from '../../filter/facet-list/mobile-facet-list.component';
import { FilterModule } from '../../filter/filter.module';
import { FormElementsModule } from '../../form-elements/form-elements.module';
import { ISelectOptions } from '../../form-elements/select/select.component';
import { LoadMoreModule } from '../../load-more/load-more.module';
import { MetaService } from '../../meta/meta.service';
import { IPageFacets } from '../../models/filter.models';
import { ProductItemModule } from '../../product/product-item.module';
import { ProductModule } from '../../product/product.module';
import { RichTextModule } from '../../rich-text/rich-text.module';
import { FeatureDetectionService } from '../../utils/helpers/feature-detection.service';
import { TrackingService } from '../../utils/tracking/tracking.service';
import { FilterPageService } from './filter-page.service';
import { IRadioGroupItem } from '../../form-elements/radio-button/radio-button.component';
import { IActiveLeasingView, LEASING_PLAN_TYPES, LeasingService } from '../../leasing/leasing.service';
import { FILTER_FACET } from '../../filter-service/filter.models';
import { BreadcrumbsModule } from '../../breadcrumbs/breadcrumbs.module';
import { SpotsUiModule } from '../../spots/spots-ui.module';
import { resolveMediaUrl } from '../../utils/helpers/media-helpers';
import { Environment, ENVIRONMENT } from '../../environment/environment';

@Component({
    selector: 'app-filter-page',
    template: `
        <section class="page-grid-content-top" *ngIf="data?.pageTopGrid">
            <app-spot-grid [grid]="data?.pageTopGrid"></app-spot-grid>
        </section>

        <section class="filter-page">
            <section class="filter-page__facets" *ngIf="(isLargeDevice$ | async) && (facets$ | async); else skeletonFacets">
                <app-facet-list [facets]="facets$ | async"></app-facet-list>
            </section>

            <ng-template #skeletonFacets>
                <section class="filter-page__facets filter-page__skeleton">
                    <app-facets-skeleton-grid [count]="2"></app-facets-skeleton-grid>
                    <app-facets-skeleton-grid [count]="6"></app-facets-skeleton-grid>
                    <app-facets-skeleton-grid [count]="7"></app-facets-skeleton-grid>
                    <app-facets-skeleton-grid [count]="6"></app-facets-skeleton-grid>
                </section>
            </ng-template>

            <!-- Filter facets -->
            <section class="filter-page__main" *ngIf="facetData$ | async as facetData; else skeletonGrid">
                <app-breadcrumbs></app-breadcrumbs>

                <div class="filter-page__head">
                    <h1 class="h2" *ngIf="data && data.pageTitle">
                        {{ data.pageTitle | replace: { searchResultCount: '' + (facetData.total || 0) } }}
                    </h1>
                    <ng-template [ngTemplateOutlet]="FilterText"></ng-template>
                </div>

                <app-highlighted-filter></app-highlighted-filter>

                <div class="scroll-sticky-wrapper"
                    #stickyScrollRef
                    [ngClass]="{ 'scroll-sticky-wrapper--is-sticky': isFixed }"
                    [ngStyle]="{ height: pageFilterElementHeight }"
                    >
                    <div class="filter-page__filter-and-sorting" #pageFilterRef [style.top]="moveMenuDown ? headerHeight + 'px' : ''">
                        <h6 *ngIf="facetData.total && !(isLargeDevice$ | async)" class="filter-page__result-count">
                            {{ 'filterpage.car_count' | translate: { number: facetData.total } }}
                        </h6>

                        <button
                            class="button--primary filter-page__show-facets-overlay"
                            *ngIf="!(isLargeDevice$ | async)"
                            (click)="showFacetsOverlay()"
                        >
                            <i class="button__icon button__icon--margin-right"
                                >${'<svg #adjustIcon class="svg-adjust-icon svg-icon" viewBox="0 0 470 470"><style>.st0{fill:none;stroke:currentColor;stroke-width:15;stroke-miterlimit:10}</style><circle class="st0" cx="98.9" cy="212.5" r="45.4"/><circle class="st0" cx="234.3" cy="347.9" r="45.4"/><circle class="st0" cx="371.1" cy="121.6" r="45.4"/><path class="st0" d="M98.9 167V37M98.9 257.9V433M234.3 302.4V37M234.3 393.3V433M371.1 76.1V37M371.1 167v266"/></svg>'}</i
                            >
                            {{ 'filterpage.filter' | translate }}
                            <ng-container *ngIf="filterService.enrichedActiveFacets | async as activeFacets">
                                <span class="filter-page__sorting-active" *ngIf="activeFacets.length !== 0"> {{ activeFacets.length }} </span>
                            </ng-container>
                        </button>

                        <app-active-facets></app-active-facets>

                        <app-select
                            *ngIf="isLargeDevice$ | async"
                            class="filter-page__sorting"
                            cssClasses="select--secondary"
                            [label]="'filterpage.sorting____choose' | translate"
                            [options]="sortingOptions$ | async"
                            [selectedValue]="activeSorting"
                            (selectChange)="onSortChange($event)"
                        ></app-select>
                    </div>
                </div>

                <div class="leasing-toggles-plp" *ngIf="site === siteID.MOTORPOINT">
                    <form *ngIf="leasingForm && dataReady" [formGroup]="leasingForm">
                        <ng-container *ngIf="leasingTypes && leasingPurposes">
                            <div class="leasing-configurator__input-section" *ngIf="leasingTypes && leasingTypes.length > 1">
                                <div class="text-uppercase leasing-heading">{{ 'leasing.configurator____leasingtype' | translate }}
                                    <a
                                        *ngIf="leasingOptionsInfoLink?.url"
                                        appInterceptLinks
                                        [href]="leasingOptionsInfoLink?.url"
                                        [attr.target]="leasingOptionsInfoLink?.target === '' ? null : leasingOptionsInfoLink?.target"
                                        rel="noopener">
                                        <span class="button--as-link" >
                                            <span class="button--mini">i</span>
                                            {{ 'leasing.help_me_choose' | translate }}
                                        </span>
                                    </a>
                                </div>
                                <app-input-toggle-group
                                    [parentForm]="leasingForm"
                                    controlName="leasingPlanType"
                                    [group]="leasingTypes"
                                    (handleValueChange)="selectLeasingPlan($event)">
                                </app-input-toggle-group>
                            </div>
                            <div class="leasing-configurator__input-section leasing-purpose" *ngIf="leasingPurposes && leasingPurposes.length > 1">
                                <div class="text-uppercase leasing-heading">{{ 'leasing.configurator____purpose' | translate }}</div>
                                <app-input-toggle-group
                                    [parentForm]="leasingForm"
                                    controlName="leasingPurpose"
                                    [group]="leasingPurposes"
                                    (handleValueChange)="selectLeasingPurpose($event)">
                                </app-input-toggle-group>
                            </div>
                            <!-- Show Cash price toggle -->
                            <div class="leasing-configurator__input-section show-cash-price">
                                <div class="text-uppercase leasing-heading">{{'filterpage.show_cash_price' | translate }}</div>
                                <div class="toggle-container" [class.active]="showCashPrice" (click)="togglePrice()">
                                    <div class="toggle-switch"></div>
                                </div>
                            </div>
                        </ng-container>
                    </form>
                </div>

                <!-- Filter results -->
                <ng-container *ngIf="((filterService.isLoading$ | async) === false && !(filterService.isInitializing | async)) || !!(filterService.isLoadingMore$ | async)">
                    <ul class="filter-page__results-list animate-fade-in"
                        [class.filter-page__results-list--loading]="
                            (filterService.isLoading$ | async) === false && (filterService.isLoadingMore$ | async) === false
                        "
                        *ngIf="settingsService.get() | async as settings"
                    >
                        <ng-container *ngFor="let item of productItems; trackBy: trackProductItem; let i = index">
                            <!-- Determine if a product item or a product grid spot -->
                            <li [ngSwitch]="item?.make !== undefined">
                                <app-product-item
                                    *ngSwitchCase="true"
                                    [enableScrollTo]="true"
                                    [isOnPLP]="true"
                                    [sliderImageSize]="400"
                                    [product]="item"
                                    [showCashPrice]="showCashPrice"
                                    [showLeasingPrices]="data?.showLeasingPricesOnly || (filterPageService.showLeasingPrices$ | async)"
                                    [isLeasingOnlyPage]="data?.showLeasingPricesOnly ?? false">
                                </app-product-item>
                                <app-product-grid-spot [data]="item" *ngSwitchCase="false"></app-product-grid-spot>
                            </li>
                        </ng-container>
                    </ul>

                    <app-component-loader *ngIf="!!(filterService.isLoading$ | async) && (filterService.isLoadingMore$ | async) === false"></app-component-loader>

                    <app-load-more
                        (handleClick)="filterService.loadMore()"
                        [shownItems]="facetData.hits?.length"
                        [totalItems]="facetData.total"
                        [incrementSize]="filterService.take"
                        [loadMoreUrl]="filterService.loadMoreUrl()"
                        [statusText]="
                            'filterpage.load_more____progress_text' | translate: { shownCars: facetData.hits?.length, totalCars: facetData.total }
                        "
                        [buttonText]="'filterpage.load_more____button' | translate"
                        [loading]="!!(filterService.isLoading$ | async)"
                    >
                    </app-load-more>
                </ng-container>
            </section>

            <ng-template #skeletonGrid>
                <section class="filter-page__main">
                    <app-breadcrumbs></app-breadcrumbs>

                    <div class="filter-page__head">
                        <h1 class="h2" *ngIf="data && data.pageTitle">
                            {{ data.pageTitle | replace: { searchResultCount: '...' } }}
                        </h1>
                        <ng-template [ngTemplateOutlet]="FilterText"></ng-template>
                    </div>

                    <div class="skeleton-highlighted-filters"></div>

                    <div class="filter-page__filter-and-sorting">
                        <app-active-facets *ngIf="isLargeDevice$ | async"></app-active-facets>
                        <app-select
                            class="filter-page__sorting"
                            cssClasses="select--secondary"
                            [label]="'filterpage.sorting____choose' | translate"
                        ></app-select>
                    </div>

                    <app-product-skeleton-grid [count]="filterService.getCarsVisibleCount()"></app-product-skeleton-grid>
                </section>
            </ng-template>
        </section>

        <section class="page-grid-content">
            <app-spot-grid [grid]="data?.mainGrid"></app-spot-grid>
            <app-filter-page-seo-links></app-filter-page-seo-links>
        </section>

        <ng-template #FilterText>
            <app-rich-text
                [@expandCollapse]="descriptionExpand ? 'expand' : 'colapse'"
                *ngIf="data?.pageDescription"
                [html]="data?.pageDescription"
                class="filter-page__description"
                [class.filter-page__description--expanded]="descriptionExpand"
            ></app-rich-text>
            <button *ngIf="data?.pageDescription" class="button--as-link filter-page__description-button" type="button" (click)="onDescriptionClick()">
                {{ (descriptionExpand ? 'buttons.read_less_text' : 'buttons.read_more_text') | translate }}
            </button>
        </ng-template>
    `,
    animations: [
        trigger('expandCollapse', [
            state(
                'expand',
                style({
                    height: '*',
                })
            ),
            state(
                'colapse',
                style({
                    height: '54px',
                })
            ),
            transition('expand <=> colapse', [animate('0.2s cubic-bezier(0.23, 0, 0.13, 1)')]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
class FilterPageComponent implements OnInit, AfterContentChecked, OnDestroy {
    @Input() data?: Cms.UsedCarsOverviewPage;
    @ViewChild('stickyScrollRef') stickyScrollRef: ElementRef;
    @ViewChild('pageFilterRef') pageFilterRef: ElementRef;
    deactivateScrollTrigger: boolean;
    @Input() showCashPrice: boolean = false;

    // Sticky scroll bar
    pageFilterElement: HTMLElement;
    pageFilterElementHeight: string;
    scrollElement: HTMLElement;
    yPosition: number;
    isFixed: boolean;
    moveMenuDown: boolean;
    headerHeight: number;
    prevScrollPosition: number;

    siteID = SiteIds;
    site: string;

    leasingTypes: IRadioGroupItem[] = [];
    leasingPurposes: IRadioGroupItem[] = [];

    leasingType: string;
    leasingPurpose: string;

    leasingTypes$: Observable<IRadioGroupItem[]>;
    leasingPurposes$: Observable<IRadioGroupItem[]>;

    currentLeasingTypeFormValue?: string;
    currentLeasingPurposeFormValue?: string;

    leasingForm: UntypedFormGroup;
    dataReady: boolean;
    leasingOptionsPlanState: string;
    leasingOptionsPurposeState: string;
    leasingOptionsInfoLink: ILink;

    sortingOptions$: Observable<ISelectOptions[]>;
    facetData$: Observable<IFilterResponse | null>;
    facets$: Observable<IPageFacets>;
    activeSorting: string;
    globalSettings: Cms.SettingsDto;
    breakpoints = BREAKPOINTS;
    descriptionExpand = false;
    lastFilterURL: string;
    overviewPageUrl: string;
    private productGridSpotsIndexCounter = 0;
    private hasAddedInitialProductGridSpot = false;
    private unsubscribe = new Subject<void>();
    productItems: any[] = [];
    isServer: boolean;


    get isLargeDevice$() {
        return this.breakpointService.matchBreakpoint(BREAKPOINTS.LARGE);
    }

    @HostListener('window:popstate', ['$event'])
    onPopState() {
        this.initiateFilter();
    }

    constructor(
        @Inject(ENVIRONMENT) private environment: Environment,
        private readonly cd: ChangeDetectorRef,
        private readonly overlayService: OverlayService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly location: Location,
        public readonly breakpointService: BreakpointService,
        public readonly filterPageService: FilterPageService,
        public readonly filterService: FilterService,
        public readonly settingsService: SettingsService,
        private readonly metaService: MetaService,
        private readonly trackingService: TrackingService,
        private readonly headerService: HeaderService,
        private featureDetectionService: FeatureDetectionService,
        private readonly fb: UntypedFormBuilder,
        private leasingService: LeasingService,
    ) {
        this.featureDetectionService.onResize$.pipe(takeUntil(this.unsubscribe)).subscribe((viewport) => {
            if (viewport && this.stickyScrollRef) {
                // Set a potentially new Y position of component
                this.yPosition = this.stickyScrollRef.nativeElement.getBoundingClientRect().top + window.scrollY;

                if (this.pageFilterRef) {
                    this.pageFilterElement = this.pageFilterRef.nativeElement;
                    this.pageFilterElementHeight = `${this.pageFilterElement.clientHeight}px`;
                }
            }
        });

        this.isServer = this.featureDetectionService.isServer();

        this.showCashPrice = this.filterService.getShowCashPrice();

        // Get sorting from url
        const params = this.activatedRoute.snapshot.queryParams;
        for (const key in params) {
            if (typeof params[key] !== 'undefined') {
                if (key === 'sort') {
                    this.activeSorting = params[key];
                }
            }
        }

        settingsService.get().pipe(takeUntil(this.unsubscribe)).subscribe((settings) => {
            this.site = settings.siteNameId;
            this.leasingOptionsInfoLink = settings.leasingCalculator.leasingInfoLink;
        });

        this.leasingTypes$ = settingsService.get().pipe(map((settings) => settings.leasingCalculator.leasingTypes.map((type: { label: string, key: string }) => ({
            label: type.label,
            value: type.key,
        }))));

        this.leasingPurposes$ = settingsService.get().pipe(map((settings) => settings.leasingCalculator.leasingPurposes.map((type: { label: string, key: string }) => ({
            label: type.label,
            value: type.key,
        }))));

    }

    ngOnInit() {
        // Update filter service, to get correct state of leasing only
        this.filterService.setLeasingOnlyBoolean(!!this.data?.showLeasingPricesOnly);

        this.sortingOptions$ = this.filterPageService.sortingOptions$;
        this.facets$ = this.filterPageService.facets$;

        this.filterService.showCashPrice$.subscribe((value) => {
            this.showCashPrice = value;
        });

        // Get leasing types for MOTORPOINT
        if (this.site === this.siteID.MOTORPOINT) {

            const settings$ = this.settingsService.get();
            const activeLeasingToggles$: Observable<IActiveLeasingView | undefined> = this.leasingService.getActiveLeasingToggles();

            const connectStream$ = combineLatest([settings$, activeLeasingToggles$]);

            connectStream$.pipe(take(1)).subscribe(([settings, activeLeasingToggles]) => {
                this.leasingTypes = settings.leasingCalculator.leasingTypes.map((type: any) => ({
                    label: type.label,
                    value: type.key,
                }));

                this.leasingPurposes = settings.leasingCalculator.leasingPurposes.map((type: any) => ({
                    label: type.label,
                    value: type.key,
                }));

                if (activeLeasingToggles?.leasingType && activeLeasingToggles?.leasingPurpose) {
                    this.currentLeasingTypeFormValue = activeLeasingToggles?.leasingType;
                    this.currentLeasingPurposeFormValue = activeLeasingToggles?.leasingPurpose;
                } else {
                    this.currentLeasingTypeFormValue = LEASING_PLAN_TYPES.TYPE_FINANCIAL;
                    this.currentLeasingPurposeFormValue = LEASING_PLAN_TYPES.PURPOSE_BUSINESS;

                    // Set default toggles for Motorpoint
                    this.leasingService.setActiveLeasingToggles({
                        leasingType: this.currentLeasingTypeFormValue,
                        leasingPurpose: this.currentLeasingPurposeFormValue
                    });
                }

                // Applying initial values to form
                this.leasingForm = this.fb.group(
                    {
                        leasingPlanType: [this.currentLeasingTypeFormValue],
                        leasingPurpose: [this.currentLeasingPurposeFormValue],
                    },
                    {
                        updateOn: 'change',
                    }
                );

                this.dataReady = true;
                this.cd.markForCheck();
            });
        }

        this.facetData$ = this.filterService.filterData.pipe(
            map((data) => {
                // Add product grid spots
                if (data) {
                    this.productItems.length = 0;
                    this.productItems = this.productItems.concat(data.hits);
                    this.hasAddedInitialProductGridSpot = false;

                    // Add product grid spots
                    if (this.data?.productGridSpots && this.data?.productGridSpots.length && data?.hits) {
                        this.productGridSpotsIndexCounter = 0;
                        const productGridSpotsStartingPosition = this.data?.spotsStartingPosition
                            ? this.data?.spotsStartingPosition
                            : this.data?.spotsInterval;
                        let productGridSpotsInterval = productGridSpotsStartingPosition + this.data?.spotsInterval;

                        for (const spot of this.data?.productGridSpots) {
                            if (productGridSpotsInterval >= data.hits?.length) {
                                break;
                            }

                            if (!this.hasAddedInitialProductGridSpot) {
                                this.productItems.splice(productGridSpotsStartingPosition - 1, 0, spot);
                                this.productGridSpotsIndexCounter = productGridSpotsStartingPosition;
                                this.hasAddedInitialProductGridSpot = true;
                            } else if (this.productGridSpotsIndexCounter <= data.hits?.length) {
                                this.productItems.splice(productGridSpotsInterval - 1, 0, spot);
                                this.productGridSpotsIndexCounter = productGridSpotsStartingPosition + this.data?.spotsInterval;
                                productGridSpotsInterval += this.data?.spotsInterval;
                            }
                        }

                        if (this.data?.repeatGridSpots) {
                            // Add additional spots to the rest of the productItems array
                            let counter = 0;

                            for (let i = 0; i < this.productItems.length; i++) {
                                if (counter >= this.data?.productGridSpots.length) {
                                    counter = 0;
                                }

                                if (i === productGridSpotsInterval) {
                                    this.productItems.splice(i, 0, this.data?.productGridSpots[counter]);
                                    productGridSpotsInterval += this.data?.spotsInterval;
                                    counter++;
                                }
                            }
                        }
                    }
                }

                this._setMetaData();
                this.metaService.setManualCanonical(this.location.path().split('?')[0]);

                return data;
            })
        );

        this.settingsService
            .get()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((settings) => {
                this.globalSettings = settings;

                this.initiateFilter();
            });

        this.filterService.activeFacets.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.onFacetChange());

        this.filterService.filterData.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            const facets: Record<string, string[]> = {};
            let contentIds: string[] = [];

            if (data) {
                // Find all selected facets and put the keyvalues in the `facets` record
                for (const facetKey in data?.facets) {
                    if (data.facets[facetKey]) {
                        const validItems = data.facets[facetKey].items?.filter((item) => item.selected).map((item) => item.key!);

                        if (validItems?.length) {
                            facets[facetKey] = validItems;
                        }
                    }
                }

                // List all ids of cars (contentIds for tracking)
                if (data.hits) {
                    contentIds = data.hits.map((hit) => hit.id!.toString());
                }
            }

            this.trackingService.trackVirtualPageViewFilter({ facets, contentIds });
        });

        // Sticky menu
        this.headerService
            .getScrollPosition()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((position: number) => {
                this.handleHeaderPosition(position);
            });

        this.headerService
            .getHeaderEvents()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((height: number) => {
                this.headerHeight = height;
                this.prevScrollPosition = height;
                this.cd.markForCheck();
            });
    }

    // Leasing plan toggle handler
    selectLeasingPlan(val: IRadioGroupItem) {
        this.currentLeasingTypeFormValue = val.value;

        this.leasingService.setActiveLeasingToggles({
            leasingType: this.currentLeasingTypeFormValue,
            leasingPurpose: this.currentLeasingPurposeFormValue
        });
    }

    // Leasing purpose toggle handler
    selectLeasingPurpose(val: IRadioGroupItem) {
        this.currentLeasingPurposeFormValue = val.value;

        this.leasingService.setActiveLeasingToggles({
            leasingType: this.currentLeasingTypeFormValue,
            leasingPurpose: this.currentLeasingPurposeFormValue
        });
    }

     // Toggel cash price
    togglePrice(): void {
        this.showCashPrice = !this.showCashPrice; // if property value is not set here, toggel becomes disabled on FE
        this.filterService.setShowCashPrice(this.showCashPrice);
    }

    resetFilters() {
        this.filterService.resetFacets();
        this.filterService.setShowCashPrice(this.showCashPrice);
    }

    initiateFilter() {
        const { b2bUsedCarsOverviewPage, usedCarsOverviewPage } = this.globalSettings.globalPages;
        const urlSegments = this.activatedRoute.snapshot.url;
        const currentRoute = `/${urlSegments.map(x => x.path).join('/')}`;

        this.overviewPageUrl = currentRoute.includes(b2bUsedCarsOverviewPage?.url)
            ? b2bUsedCarsOverviewPage?.url
            : currentRoute.includes(usedCarsOverviewPage?.url)
            ? usedCarsOverviewPage?.url
            : '/';

        const pageSpecificParams: Params = {};

        if (this.data?.initialFilterFacetList?.length) {

            // Javascript does not allow duplicate keys in object
            // But we can add an array of values to each key,
            // and it will be fetched as a unique param value in our filter results request
            // Example: {
            //  dealer.dealerAlternativeName.keyword: ['Esbjerg', 'Esbjerg Fordstore'],
            //  make.keyword: ['Volvo', 'BMW']
            // }
            this.data?.initialFilterFacetList
                .split(',')
                .filter((i) => i) // removing and "" from the array
                .forEach((keyValyeString) => {
                    const [key, value] = keyValyeString.trim().split('=');

                    // Push duplicate values to existing key object array
                    if (pageSpecificParams[key]) {
                        pageSpecificParams[key].push(value);
                    } else {
                        // Create an object array for any unique key
                        pageSpecificParams[key] = [value];
                    }
                });
        } else {
            // This url-to-params logic needs to be replaced by the new megamenu structure (BND-1079) for the new leasingPage logic to properly work (BND-1068)
            const [make, model] = currentRoute.split('/').splice(2);
            if (make) pageSpecificParams[FILTER_FACET.MAKE] = make;
            if (model) pageSpecificParams[FILTER_FACET.MODEL] = model;
        }

        this.filterService.initFilter(this.overviewPageUrl, { initialFilters: pageSpecificParams, ...this.activatedRoute.snapshot.queryParams });
    }

    ngAfterContentChecked(): void {
        if (this.featureDetectionService.isBrowser()) {
            if (this.stickyScrollRef) {
                this.scrollElement = this.stickyScrollRef.nativeElement;
                if (this.stickyScrollRef.nativeElement.getBoundingClientRect) {
                    this.yPosition = this.stickyScrollRef.nativeElement.getBoundingClientRect()?.top + window.scrollY;
                }
            }
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    handleHeaderPosition(position: number) {
        position = 0; // Make filter header stay in top

        if (window.scrollY < this.yPosition - this.headerHeight) {
            this.isFixed = false;
            this.moveMenuDown = false;
            this.pageFilterElementHeight = 'auto';
        } else {
            this.moveMenuDown = position < this.prevScrollPosition;
        }
        if (window.scrollY >= this.yPosition) {
            this.isFixed = true;
            this.pageFilterElementHeight = `${this.scrollElement.clientHeight}px`;
        }
        this.prevScrollPosition = position;
        this.cd.markForCheck();
    }

    onFacetChange() {
        this.cd.markForCheck();
    }

    onSortChange(value: string | number) {
        this.filterService.setSorting(`${value}`);
    }

    showFacetsOverlay() {
        this.overlayService.open(MobileFacetListComponent, {
            panelClass: 'mobile-overlay-panel',
            data: {
                facets: this.facets$,
            },
            fullHeight: true,
        });
    }

    onDescriptionClick() {
        this.descriptionExpand = !this.descriptionExpand;
    }

    trackProductItem(index: number, product: any) {
        if (product && product.id) {
            return product.id;
        }
        return index;
    }

    private _setMetaData(): void {
        if (!this.data?.metaTitle) return;

        const { metaDescription: description, metaCanonical: canonical, excludeFromRobots: excludeFromRobots, soMeImage: image, metaKeywords: keywords, metaTitle: title } = this.data;


        // SEO-related information extracted from the data object
        this.metaService.setTitle(title);
        this.metaService.setDescription(description);
        this.metaService.setKeywords(keywords);
        this.metaService.setNoIndex(excludeFromRobots);
        this.metaService.setCanonical(canonical?.url);

        // Open graph
        this.metaService.setOpenGraph({
            title: title || '',
            image: image
                ? {
                      url: resolveMediaUrl(image.src, this.environment, { width: 1200, height: 630, rmode: 'crop' }),
                      altText: image.alt,
                      width: '1200',
                      height: '630',
                  }
                : undefined,
            type: 'website',
            author: this.globalSettings?.companyName,
        });
    }
}

@NgModule({
    declarations: [FilterPageComponent],
    imports: [
        FilterModule,
        CommonModule,
        LoadMoreModule,
        FormElementsModule,
        FormsModule,
        ReactiveFormsModule,
        UtilsModule,
        TranslateModule.forChild(),
        OverlayModule,
        ProductModule,
        ProductItemModule,
        RichTextModule,
        ComponentLoaderModule,
        BreadcrumbsModule,
        SpotsUiModule
    ]
})
class FilterPageModule {}

export const LazyComponent = FilterPageComponent;
